import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getEnvelopeBySlug } from '@/services/api';
import type { EnvelopeDetail } from '@/types';
import LetterContent from '@/components/LetterContent';

const EnvelopePage = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [envelope, setEnvelope] = useState<EnvelopeDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [isOpening, setIsOpening] = useState(false);
    const [isOpened, setIsOpened] = useState(false);

    useEffect(() => {
        const fetchEnvelope = async () => {
            if (!slug) return;

            try {
                const data = await getEnvelopeBySlug(slug);
                setEnvelope(data);

                // Start opening animation after a short delay
                setTimeout(() => setIsOpening(true), 500);
                setTimeout(() => setIsOpened(true), 1500);
            } catch (error) {
                console.error('Failed to fetch envelope:', error);
                navigate('/');
            } finally {
                setLoading(false);
            }
        };

        fetchEnvelope();
    }, [slug, navigate]);

    if (loading || !envelope) {
        return (
            <div className="min-h-screen bg-vintage-cream paper-texture flex items-center justify-center">
                <div className="text-vintage-brown text-xl font-serif">Opening letter...</div>
            </div>
        );
    }

    // Get style-specific classes
    const getStyleClasses = () => {
        switch (envelope.style) {
            case 'sepia':
                return 'sepia-filter';
            case 'ink':
                return 'text-gray-800';
            default:
                return '';
        }
    };

    return (
        <div className="min-h-screen bg-vintage-cream paper-texture py-8 px-4">
            {/* SEO Meta Tags */}
            <title>{envelope.title} - ~sincerelyyourcaffeine</title>
            <meta name="description" content={envelope.body.substring(0, 155)} />
            <meta property="og:title" content={envelope.title} />
            <meta property="og:description" content={envelope.body.substring(0, 155)} />
            <meta property="og:type" content="article" />

            {/* Back Button */}
            <div className="container mx-auto max-w-4xl mb-6">
                <Link
                    to="/"
                    className="inline-flex items-center text-vintage-brown hover:text-vintage-darkbrown font-serif transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to all letters
                </Link>
            </div>

            {/* Envelope Opening Animation */}
            {!isOpened && (
                <div className="container mx-auto max-w-4xl mb-8 flex justify-center">
                    <div className={`transform transition-all duration-1000 ${isOpening ? 'animate-envelope-open' : ''}`}>
                        <svg
                            className="w-48 h-48 text-vintage-brown"
                            viewBox="0 0 100 80"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect x="5" y="15" width="90" height="60" fill="currentColor" opacity="0.3" />
                            <path d="M5 15 L50 45 L95 15" stroke="currentColor" strokeWidth="2" fill="none" />
                            <path d="M5 15 L50 40 L95 15 L95 20 L50 50 L5 20 Z" fill="currentColor" opacity="0.5" />
                        </svg>
                    </div>
                </div>
            )}

            {/* Letter Content */}
            <div className={`container mx-auto max-w-4xl transition-all duration-800 ${isOpened ? 'opacity-100 animate-letter-unfold' : 'opacity-0'}`}>
                <div className={`letter-content ${getStyleClasses()}`}>
                    {/* Decorative stamp */}
                    <div className="absolute top-4 right-4 stamp text-vintage-brown text-xs font-serif opacity-60">
                        {new Date(envelope.created_at).getFullYear()}
                    </div>

                    {/* Title */}
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-vintage-ink mb-6 text-center">
                        {envelope.title}
                    </h1>

                    {/* Date */}
                    <p className="text-center text-vintage-brown font-body mb-8">
                        {new Date(envelope.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        })}
                    </p>

                    {/* Letter Body */}
                    <LetterContent body={envelope.body} />
                </div>
            </div>

            {/* Spacing */}
            <div className="h-20" />
        </div>
    );
};

export default EnvelopePage;
